/**
 * Ordre d’affichage des blocs sur Home/Boutique (`sortOrder` API + overrides locaux au drag-and-drop).
 */
import type { WidgetWorkspaceWidgetWorkspaceReadModel } from '@r/api/generated/client.schemas.ts';
import type { ContentBlockPageId } from '@r/helpers/widget-workspace-slides.ts';

type WidgetWorkspace = WidgetWorkspaceWidgetWorkspaceReadModel;

/** Tri stable par `sortOrder` API (puis id). */
export const compareBlocksBySortOrder = (left: WidgetWorkspace, right: WidgetWorkspace): number => {
  const leftOrder = left.sortOrder ?? 0;
  const rightOrder = right.sortOrder ?? 0;
  if (leftOrder !== rightOrder) {
    return leftOrder - rightOrder;
  }
  return (left.id ?? 0) - (right.id ?? 0);
};

export const sortBlocksBySortOrder = (blocks: WidgetWorkspace[]): WidgetWorkspace[] =>
  [...blocks].sort(compareBlocksBySortOrder);

export const getOrderedBlockIdsFromServer = (blocks: WidgetWorkspace[]): number[] =>
  sortBlocksBySortOrder(blocks)
    .map((block) => block.id)
    .filter((id): id is number => typeof id === 'number');

/** Prochain `sortOrder` pour un nouveau bloc sur la page (après le dernier). */
export const getNextSortOrderForPage = (blocks: WidgetWorkspace[]): number => {
  if (blocks.length === 0) return 0;
  const sorted = sortBlocksBySortOrder(blocks);
  const last = sorted[sorted.length - 1];
  return (last?.sortOrder ?? sorted.length - 1) + 1;
};

/** Réordonne un tableau (utilisé pour l’ordre visuel des blocs). */
export const reorderList = <T>(list: T[], fromIndex: number, toIndex: number): T[] => {
  if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) {
    return list;
  }

  const next = [...list];
  const [moved] = next.splice(fromIndex, 1);
  if (moved === undefined) {
    return list;
  }
  next.splice(toIndex, 0, moved);
  return next;
};

/** Fusionne l’ordre local avec les ids serveur (nouveaux blocs en fin de liste). */
export const mergeBlockOrder = (
  localOrder: number[] | undefined,
  serverIds: number[]
): number[] => {
  if (!localOrder || localOrder.length === 0) {
    return serverIds;
  }

  const serverIdSet = new Set(serverIds);
  const merged = localOrder.filter((id) => serverIdSet.has(id));
  const mergedIdSet = new Set(merged);

  for (const id of serverIds) {
    if (!mergedIdSet.has(id)) {
      merged.push(id);
      mergedIdSet.add(id);
    }
  }

  return merged;
};

/** Ordre visuel temporaire par onglet hub, avant sync API ou en cas d’échec PATCH. */
export type BlockOrderOverrides = Partial<Record<ContentBlockPageId, number[]>>;

export const hasOrderChangedFromServer = (
  order: number[] | undefined,
  serverIds: number[]
): boolean => {
  if (!order) {
    return false;
  }
  return order.join(',') !== serverIds.join(',');
};

/** Entrées PATCH `{ sortOrder }` pour aligner l’API sur l’ordre local. */
export const buildSortOrderPatches = (
  orderedIds: number[],
  blocksById: Map<number, WidgetWorkspace>
): { id: number; sortOrder: number }[] => {
  const patches: { id: number; sortOrder: number }[] = [];

  for (let sortOrder = 0; sortOrder < orderedIds.length; sortOrder++) {
    const id = orderedIds[sortOrder];
    if (id === undefined) continue;

    const block = blocksById.get(id);
    if (block !== undefined && (block.sortOrder ?? 0) !== sortOrder) {
      patches.push({ id, sortOrder });
    }
  }

  return patches;
};
